import Tree from 'components/tree/Tree';
import { COURSE_ICONS, COURSE_ICONS_PATH } from 'constants/course-icons';
import {
  Col,
  Divider,
  Flex,
  Form,
  FormProps,
  Image,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography
} from 'antd';
import {
  ICourseRequirement,
  ICourseShow,
  ICourseTree
} from 'interfaces/courses';
import { IInstructor } from 'interfaces/instructors';
import { IProductCategory } from 'interfaces/product_categories';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelect } from '@refinedev/antd';

interface TabInfoProps {
  formProps: FormProps;
  onFinish: (values: ICourseShow) => void;
  course?: ICourseShow;
}

export const TabInfo = ({ formProps, onFinish, course }: TabInfoProps) => {
  const [courseType, setCourseType] = useState<'regular' | 'on_demand'>(
    course?.course_type || 'regular'
  );

  useEffect(() => {
    if (course?.course_type) {
      setCourseType(course.course_type);
    }
  }, [course?.course_type]);

  const { selectProps: instructorSelectProps } = useSelect<IInstructor>({
    resource: 'instructors',
    optionLabel: 'name',
    optionValue: 'id',
    defaultValue: course?.instructor_ids,

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { selectProps: productCategorySelectProps } =
    useSelect<IProductCategory>({
      resource: 'products/categories',
      optionLabel: 'name',
      optionValue: 'name',

      onSearch: value => [
        {
          field: 'filter[by_name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server'
      }
    });

  const updateCourse = useCallback(
    (tree: ICourseTree, updatedRequirement?: ICourseRequirement) => {
      if (!course) {
        return;
      }

      let updatedRequirements = course.requirements;

      if (updatedRequirement) {
        updatedRequirements = course.requirements.map(req =>
          req.id === updatedRequirement.id ? updatedRequirement : req
        );

        formProps.form?.setFieldValue('requirements', updatedRequirements);
      }

      formProps.form?.setFieldValue('tree', tree);
    },
    [course, formProps.form]
  );

  const icon = useMemo(
    () => formProps.initialValues?.media?.icon || COURSE_ICONS[2],
    [formProps.initialValues?.media?.icon]
  );

  const onChangeIcon = useCallback(
    (e: RadioChangeEvent) => {
      const value = e.target.value;
      formProps.form?.setFieldValue(['media', 'icon'], value);
    },
    [formProps.form]
  );

  return (
    <Form {...formProps} onFinish={onFinish} layout="vertical">
      <Form.Item
        label="Nome"
        name="name"
        rules={[
          {
            required: true
          }
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Duração (horas)"
        name="duration_in_hours"
        rules={[
          {
            required: true
          }
        ]}
      >
        <InputNumber min={1} />
      </Form.Item>

      <Form.Item
        label="Nota mínima (0 - 100)"
        name="min_evaluation_score"
        rules={[
          {
            required: true
          }
        ]}
      >
        <InputNumber min={0} max={100} />
      </Form.Item>

      <Form.Item
        label="Tipo de emissão de certificado"
        name="certificate_automatic"
        extra={
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            <strong>Automática:</strong> O aluno pode emitir o certificado diretamente pela
            plataforma.
            <br />
            <strong>Manual:</strong> A emissão do certificado é feita pela secretaria.
          </p>
        }
      >
        <Radio.Group>
          <Radio.Button value={true}>Automática</Radio.Button>
          <Radio.Button value={false}>Manual</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {course?.unit_name && (
        <p style={{ marginTop: 10, marginBottom: 16, color: '#595959' }}>
          <strong>Unidade/Campus: </strong>{course.unit_name}
        </p>
      )}

      <Divider />

      <Typography.Title level={5}>Configuração de curso</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={6}>
          <Form.Item
            label="Tipo de curso"
            name="course_type"
            initialValue="regular"
            style={{ marginBottom: 8 }}
            rules={[
              {
                required: true
              }
            ]}
          >
            <Radio.Group
              onChange={e => setCourseType(e.target.value)}
              value={courseType}
            >
              <Radio.Button value="regular">Regular</Radio.Button>
              <Radio.Button value="on_demand">Flexível</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>

        {courseType === 'on_demand' && (
          <Col xs={24} md={12}>
            <Form.Item
              label="Dias mínimos para aprovação"
              name="minimum_days_to_approve"
              style={{ marginBottom: 8 }}
              rules={[
                {
                  required: true,
                  message: 'Campo obrigatório para cursos flexíveis'
                }
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginBottom: 16 }}
      >
        <strong>Regular:</strong> Curso com datas de início e término definidas. O certificado é liberado somente após o fechamento de todas as turmas.
        <br />
        <strong>Flexível:</strong> O aluno pode iniciar e concluir o curso no seu próprio ritmo. O certificado é liberado após a conclusão do curso, respeitando o prazo mínimo configurado.
      </Typography.Text>

      <Divider />

      <Form.Item label="Instrutores" name="instructor_ids">
        <Select {...instructorSelectProps} mode="multiple" />
      </Form.Item>

      <Form.Item label="Conteúdos gratuitos" name="free_content_ids">
        <Select mode="multiple">
          {course?.tree_contents.map(content => (
            <Select.Option key={content.id}>{content.name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      
      <Divider />

      <Typography.Title level={5}>
        Personalização e categorização
      </Typography.Title>

      <Typography.Text
        className="block text-neutral-500"
        style={{ marginBottom: 16 }}
      >
        Adicione um ícone e etiqueta para representar e classificar seu curso.
        Essas informações ajudam na organização e poderão ser alteradas depois.
      </Typography.Text>

      <Form.Item
        label="Etiquetas"
        name={['media', 'tags']}
        extra={
          <Typography.Text type="secondary" className="block mt-2">
            Para editar as etiquetas entre em Loja Online `{'>'}` Categorias
          </Typography.Text>
        }
      >
        <Select
          {...productCategorySelectProps}
          placeholder="Selecione uma ou mais etiquetas..."
          mode="multiple"
        />
      </Form.Item>

      <Form.Item name={['media', 'icon']} label="Ícone" initialValue={icon}>
        <Typography.Text
          type="secondary"
          className="block text-secondary-500 mt-0 mb-4"
        >
          Escolha o ícone que irá aparecer no card da tela de cursos do aluno
        </Typography.Text>

        <Radio.Group
          rootClassName="course-icons-radio-wrapper"
          onChange={onChangeIcon}
          defaultValue={icon}
          options={COURSE_ICONS.map((icon, index) => ({
            value: icon,
            label: (
              <Flex vertical gap={4}>
                <Image
                  preview={false}
                  src={`${COURSE_ICONS_PATH}/${icon}`}
                  alt={icon}
                  width={50}
                  height={50}
                />
                {index === 0 && (
                  <Typography.Text type="secondary">Padrão</Typography.Text>
                )}
              </Flex>
            )
          }))}
        />
      </Form.Item>

      {course && !course.with_disciplines && (
        <>
          <Divider />

          <Typography.Title level={5}>Conteúdo</Typography.Title>

          <Form.Item name="tree">
            {course && (
              <Tree
                unit={{
                  id: course.id,
                  kind: 'course',
                  requirements: course.requirements
                }}
                tree={course.tree}
                onUpdate={updateCourse}
              />
            )}
          </Form.Item>
        </>
      )}
    </Form>
  );
};
